<template>
  <AppDialog
    :open="open"
    @update:open="close"
    :title="t('nkf.promptDetails.dryTitle')"
    :description="t('nkf.promptDetails.dryUpdated')"
  >
    <template #illustration
      ><AppDialogIllustration src="/images/nkf/dryw.webp" background="var(--card)" width="70%"
    /></template>
    <div
      class="dry-update-values tw:grid tw:items-center tw:bg-(--surface-dialog) tw:text-center tw:gap-1.5 tw:-mt-0.5 tw:mx-0 tw:mb-0.5 tw:py-3.5 tw:px-2.5 tw:rounded-(--radius-card)"
    >
      <div>
        <small class="tw:type-meta tw:text-(--muted-foreground) tw:block">{{
          t('nkf.promptDetails.previous')
        }}</small>
        <strong
          class="tw:type-hero tw:text-(--foreground) tw:block tw:my-0.5 tw:mx-0 tw:tracking-normal"
          >{{ update.previousWeight ?? '-' }} kg</strong
        >
        <span
          class="tw:type-micro tw:text-(--subtle-foreground)"
          v-if="update.previousRecordedTimeRaw"
          >{{
            t('nkf.promptDetails.since', {
              date: DateUtils.formatServerDateTime(
                update.previousRecordedTimeRaw,
                DateUtils.getClientDateTimeConfig().dayMonthShort,
              ),
            })
          }}</span
        >
      </div>
      <AppIcon
        size="control"
        name="arrowR"
        class="dry-update-values__arrow tw:text-(--subtle-foreground)"
      />
      <div class="dry-update-values__new">
        <small class="tw:type-meta tw:text-(--muted-foreground) tw:block">{{
          t('nkf.promptDetails.new')
        }}</small>
        <strong class="tw:type-hero tw:text-(--link) tw:block tw:my-0.5 tw:mx-0 tw:tracking-normal"
          >{{ update.weight }} kg</strong
        >
        <span v-if="update.recordedTimeRaw" class="tw:type-micro tw:text-(--subtle-foreground)">{{
          effectiveDateLabel
        }}</span>
      </div>
    </div>
    <template #footer
      ><Button :disabled="isClosing" :aria-busy="isClosing" @click="close">{{
        t('nkf.close')
      }}</Button></template
    >
  </AppDialog>
</template>
<script setup>
// 1. Import necessary components
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import DateUtils from '@/shared/utilities/date-utils';
import fluidService from '@/shared/services/fluid.service';
import { useFluidStore } from '@/core/stores/fluid.store';
import { useDryWeightUpdateStore } from '@/core/stores/dry-weight-update.store';
import { useNotificationsStore } from '@/core/stores/notifications.store';
import AppDialog from '@/shared/components/AppDialog.vue';
import AppDialogIllustration from '@/shared/components/AppDialogIllustration.vue';
import AppIcon from '@/shared/components/AppIcon.vue';
import { Button } from '@/shared/components/ui/button';
// 2. Use
const open = defineModel('open', { type: Boolean, default: false });
const props = defineProps({
  // Display values and the prescription identity; the Home notification has its own itemUnique.
  update: { type: Object, required: true },
});
const { t } = useI18n();
const dryWeightUpdates = useDryWeightUpdateStore();
const inbox = useNotificationsStore();
const fluid = useFluidStore();
// 3. Define a reactive variable
const isClosing = ref(false);
// 4. Define methods or computed properties if needed
const effectiveDateLabel = computed(() => {
  if (DateUtils.isToday(DateUtils.parseServerDate(props.update.recordedTimeRaw))) {
    return t('nkf.promptDetails.fromToday');
  }
  return t('nkf.promptDetails.since', {
    date: DateUtils.formatServerDateTime(
      props.update.recordedTimeRaw,
      DateUtils.getClientDateTimeConfig().dayMonthShort,
    ),
  });
});
// Closing acknowledges the displayed prescription and completes its notification.
// The dialog stays open when the request fails, so the patient can retry.
async function close() {
  if (isClosing.value) {
    return;
  }

  isClosing.value = true;
  try {
    const saved = await fluidService.reviewDryWeight(props.update.prescriptionItemUnique, {
      weight: props.update.weight,
    });
    fluid.applyDashboard(saved.dashboard);
    open.value = false;
    // A same-ID Portal correction remains Updated until its newly displayed value is reviewed.
    if (
      saved.dashboard.dryWeight?.itemUnique !== props.update.prescriptionItemUnique ||
      saved.dashboard.dryWeight.acknowledged
    ) {
      dryWeightUpdates.removePrescription(props.update.prescriptionItemUnique);
    }
    if (saved.dashboard.dryWeight && !saved.dashboard.dryWeight.acknowledged) {
      await dryWeightUpdates.load();
    }
    // Direct Home/Fluid actions bypass the inbox presenter; refresh its saved unread count too.
    await inbox.refreshCount();
  } finally {
    isClosing.value = false;
  }
}
// 5. VueJS Hooks
</script>
<style scoped>
/* The dialog grid adds a 1rem gap, so the margins bring the design's 14px above and 12px below. */
.dry-update-values {
  grid-template-columns: 1fr auto 1fr;
}
</style>
